import { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Share2, Trash2, Loader2, UserPlus } from 'lucide-react';

/**
 * ShareDialog - Reusable user-to-user sharing
 * Props:
 *   open, onClose
 *   recordType: 'task' | 'habit' | 'asset' | 'vehicle' | 'vendor' | 'project' | 'expense' | 'asset_sub'
 *   recordId
 *   recordName (for title)
 *   parentId, subCollection (only for asset_sub)
 */
export default function ShareDialog({ open, onClose, recordType, recordId, recordName, parentId, subCollection }) {
  const [username, setUsername] = useState('');
  const [permission, setPermission] = useState('view');
  const [existing, setExisting] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchShares = useCallback(async () => {
    if (!recordId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/record-shares/${recordType}/${recordId}`);
      setExisting(data.shares || []);
    } catch {}
    setLoading(false);
  }, [recordType, recordId]);

  useEffect(() => { if (open) { fetchShares(); setUsername(''); setPermission('view'); } }, [open, fetchShares]);

  const submit = async () => {
    if (!username.trim()) { toast.error('Username required'); return; }
    setSaving(true);
    try {
      const body = { record_type: recordType, record_id: recordId, shared_with_username: username.trim(), permission };
      if (recordType === 'asset_sub') { body.parent_id = parentId; body.sub_collection = subCollection; }
      await api.post('/share', body);
      toast.success(`Shared with @${username.trim().toLowerCase()}`);
      setUsername(''); setPermission('view');
      fetchShares();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    setSaving(false);
  };

  const updatePerm = async (shareId, perm) => {
    try { await api.put(`/share/${shareId}`, { permission: perm }); toast.success('Permission updated'); fetchShares(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const revoke = async (shareId) => {
    try { await api.delete(`/share/${shareId}`); toast.success('Access revoked'); fetchShares(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const label = recordType === 'asset_sub' ? 'record' : (recordType === 'project' ? 'workspace' : recordType);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="share-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Share2 className="h-4 w-4" /> Share {label}</DialogTitle>
          <DialogDescription className="truncate">{recordName || '\u00A0'}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex items-end gap-2">
            <div className="flex-1 space-y-1.5">
              <Label className="text-[11px] text-zinc-500 uppercase tracking-wider">Username</Label>
              <Input placeholder="Enter username" value={username} onChange={e => setUsername(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); submit(); } }}
                data-testid="share-username-input" autoFocus />
            </div>
            <Select value={permission} onValueChange={setPermission}>
              <SelectTrigger className="w-[110px]" data-testid="share-permission-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="view">View</SelectItem>
                <SelectItem value="edit">Edit</SelectItem>
                <SelectItem value="comment">Comment</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={submit} disabled={saving} data-testid="share-submit-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
            </Button>
          </div>
          <div className="border-t border-zinc-200 dark:border-zinc-800 pt-3">
            <p className="text-[11px] text-zinc-500 uppercase tracking-wider mb-2">Already shared with</p>
            {loading ? <Loader2 className="h-4 w-4 animate-spin text-zinc-400 mx-auto" />
             : existing.length === 0 ? <p className="text-xs text-zinc-400 text-center py-3">Not shared yet</p>
             : <div className="space-y-1.5 max-h-48 overflow-y-auto">
               {existing.map(s => (
                 <div key={s.id} className="flex items-center justify-between p-2 rounded-lg bg-zinc-50 dark:bg-zinc-900" data-testid={`share-entry-${s.id}`}>
                   <div className="flex items-center gap-2">
                     <div className="w-7 h-7 rounded-full bg-zinc-200 dark:bg-zinc-700 flex items-center justify-center text-[10px] font-medium">
                       {(s.shared_with_username || '?').charAt(0).toUpperCase()}
                     </div>
                     <span className="text-xs font-medium">@{s.shared_with_username}</span>
                   </div>
                   <div className="flex items-center gap-1">
                     <Select value={s.permission} onValueChange={v => updatePerm(s.id, v)}>
                       <SelectTrigger className="w-[90px] h-7 text-xs" data-testid={`share-perm-${s.id}`}><SelectValue /></SelectTrigger>
                       <SelectContent>
                         <SelectItem value="view">View</SelectItem>
                         <SelectItem value="edit">Edit</SelectItem>
                         <SelectItem value="comment">Comment</SelectItem>
                       </SelectContent>
                     </Select>
                     <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => revoke(s.id)} data-testid={`share-revoke-${s.id}`}>
                       <Trash2 className="h-3 w-3 text-red-400" />
                     </Button>
                   </div>
                 </div>
               ))}
             </div>}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
